import { useState, useEffect } from 'react';
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useForm } from "react-hook-form";
import Multiselect from 'multiselect-react-dropdown';
import { toast } from "react-toastify";
// @mui
import { styled } from '@mui/material/styles';
import { Button, Typography, Container, Box } from '@mui/material';
import * as userActions from "../../actions/userActions";
import config from "../../config";
import { encryptData } from '../../utils/cryptoUtils';
import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap/dist/js/bootstrap.bundle';
import '../common.scss';
import * as leadActions from "../../actions/leadActions";
import * as campaignsActions from "../../actions/campaignsActions";

import AccessDenied from '../common/AccessDenied';
// ----------------------------------------------------------------------

const StyledContent = styled('div')(({ theme }) => ({
    maxWidth: 480,
    margin: 'auto',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: theme.spacing(12, 0),
}));

// ----------------------------------------------------------------------

const DealerAddEdit = (props) => {
    const { id } = useParams();
    const location = useLocation();
    const { actions, usersData, checklistsData, masterDealersData } = props;
    const { register, handleSubmit, formState: { errors }, setValue } = useForm();
    const navigate = useNavigate();
    const [allUsers, setAllUsers] = useState([]);
    const [documentTypeId, setDocumentTypeId] = useState('');
    const [showDocumentTypeDetails, setShowDocumentTypeDetails] = useState(false);

    const [addModulePermissionStatus, setAddModulePermissionStatus] = useState(false);
    const [editModulePermissionStatus, setEditModulePermissionStatus] = useState(false);
    const [userHasPermission, setUserHasPermission] = useState(true);
    const [permissions, setPermissions] = useState([]);

    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState('');

    useEffect(() => {
        const obj = { 'start': '0', 'length': config.MAX_RECORDS };
        const requestData = encryptData(obj);
        const response = actions.getUserModuleData(requestData);
        response.then((result) => {
            // console.log("Promise resolved -  lead list:", result);
            if (result.data.success === 1) {
                const permissionsData = result.data.data;
                setPermissions(permissionsData);
                const leadPermissions = permissionsData.find(permission => permission.module_name === 'Dealer');
                const hasAddPermissions = leadPermissions?.add_permission;
                const hasEditPermissions = leadPermissions?.edit_permission;

                setAddModulePermissionStatus(hasAddPermissions);
                setEditModulePermissionStatus(hasEditPermissions);
                setUserHasPermission(id !== undefined ? hasEditPermissions : hasAddPermissions);
            }
        }).catch((error) => {
            console.error("Promise rejected -  lead list:", error);
        });
    }, [setPermissions, setUserHasPermission]);


    useEffect(() => {
        // check user is logged-in or not
        const authToken = localStorage.getItem('authToken');
        if (authToken === null) {
            navigate('/login');
        }

        const obj = { 'start': '0', 'length': config.MAX_RECORDS };
        const requestData = encryptData(obj);
        actions.usersData(requestData);

        if (usersData.success) {
            setAllUsers(usersData.data);
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        setShowDocumentTypeDetails(false);
        if (id !== undefined && !showDocumentTypeDetails) {
            const decodedId = atob(id);
            setDocumentTypeId(decodedId);

            const obj = { 'start': '0', 'length': config.MAX_RECORDS, 'dealer_id': decodedId };
            const requestData = encryptData(obj);

            const response = actions.masterDealersData(requestData, false);
            response.then((result) => {
                // console.log("Promise resolved -  source data:", result); 
                if (result.data.success === 1) {
                    setShowDocumentTypeDetails(true);
                    const results = result.data.data.data;
                    const resultArr = Array.isArray(results) ? results : Object.values(results || {});

                    setValue("name", resultArr[0].name);
                    setValue("benificary_name", resultArr[0].benificary_name);
                    setValue("address", resultArr[0].address);
                    setValue("email", resultArr[0].email);
                    setValue("bank_name", resultArr[0].bank_name);
                    setValue("account_no", resultArr[0].account_no);
                    setValue("ifsc", resultArr[0].ifsc);

                    setValue("prefill_template_type", resultArr[0].prefill_template_type);
                    setValue("status", resultArr[0].status);
                    const locationId = String(resultArr[0].location_id);
                    setSelectedLocation(locationId);

                    // ensure dropdown options are already loading/loaded safely
                    setTimeout(() => {
                        setValue("location_id", locationId);
                    }, 0);

                }
            }).catch((error) => {
                //  console.error("Promise rejected -  source data :", error); 
            });
        }
    }, [id, setValue, setShowDocumentTypeDetails]);

    useEffect(() => {
        if (locations.length > 0 && selectedLocation !== '') {
            setValue("location_id", selectedLocation);
        }
    }, [locations, selectedLocation, setValue]);

    useEffect(() => {
        const obj = { start: "0", length: config.MAX_RECORDS };
        const requestData = encryptData(obj);

        const response = actions.masterLocationsData(requestData);

        response.then((result) => {
            if (result.data.success === 1) {
                const results = result.data.data.data;
                const resultArr = Object.keys(results).map(key => results[key]);
                setLocations(resultArr);
            }
        }).catch((err) => {
            console.error("Location API error:", err);
        });
    }, []);

    const onSubmit = (data) => {

        if (data.name.trim() === "") {
            toast.error("Dealer is blank.", {
                position: "top-right",
            });

        } else if (data.benificary_name.trim() === "") {
            toast.error("Benificary Name is blank.", {
                position: "top-right",
            });
        } else {
            const params = { 'name': data.name, 'address': data.address, 'bank_name': data.bank_name, 'account_no': data.account_no, 'ifsc': data.ifsc, "status": data.status, "email": data.email, "benificary_name": data.benificary_name, location_id: data.location_id };

            if (documentTypeId !== '') {
                params.dealer_id = documentTypeId;
            }

            const requestData = encryptData(params);
            const response = actions.addEditDealerData(requestData);

            response.then((result) => {
                // console.log("Promise resolved - add/edit lead form:", result); 
                if (result.data.success === 1) {
                    toast.success(result.data.message, {
                        position: "top-right",
                    });
                    setTimeout(() => {
                        navigate('/dashboard/dealers');
                    }, 1000);
                } else {
                    toast.error(result.data.message, {
                        position: "top-right",
                    });
                }
            }).catch((error) => {
                // console.error("Promise rejected - add/edit lead form:", error);
                toast.error(error, {
                    position: "top-right",
                });
            });
        }
    };

    if (!userHasPermission) {
        // Redirect or render an access denied message
        //  return <AccessDenied />;
    }
    return (
        <>
            <Helmet>
                <title>{showDocumentTypeDetails ? "Edit Dealer" : "Create Dealer"}</title>
            </Helmet>

            <Container>
                <Typography variant="h4" sx={{ mb: 5 }}>
                    {showDocumentTypeDetails ? "Edit Dealer" : "Create Dealer"}
                </Typography>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <div id='main-wrap'>
                        <div className='row'>
                            <div className='col-md-6'>
                                <div className='form-group'>
                                    <label htmlFor="campaign-name">
                                        <span>Dealer <a style={{ color: "red" }}>*</a></span>
                                        <input type='text' id='name' className='form-control' {...register('name', { required: true })} />
                                    </label>
                                    {errors.name && <span className="required">This field is required.</span>}
                                </div>
                            </div>

                            <div className='col-md-6'>
                                <div className='form-group'>
                                    <label htmlFor="campaign-name">
                                        <span>Benificary Name <a style={{ color: "red" }}>*</a></span>
                                        <input type='text' id='benificary_name' className='form-control' {...register('benificary_name', { required: true })} />
                                    </label>
                                    {errors.benificary_name && <span className="required">This field is required.</span>}
                                </div>
                            </div>

                            <div className='col-md-12'>
                                <div className='form-group'>
                                    <label htmlFor="address">
                                        <span>Address </span>
                                        <input type='text' id='address' className='form-control' {...register('address', {})} />
                                    </label>
                                    {errors.address && <span className="required">This field is required.</span>}
                                </div>
                            </div>


                            <div className='col-md-6'>
                                <div className='form-group'>
                                    <label htmlFor="email">
                                        <span>Email </span>
                                        <input type='text' id='email' className='form-control' {...register('email', {})} />
                                    </label>
                                    {errors.email && <span className="required">This field is required.</span>}
                                </div>
                            </div>

                            <div className='col-md-6'>
                                <div className='form-group'>
                                    <label htmlFor="account_no">
                                        <span>Account No </span>
                                        <input type='text' id='account_no' className='form-control' {...register('account_no', {})} />
                                    </label>
                                    {errors.account_no && <span className="required">This field is required.</span>}
                                </div>
                            </div>

                            <div className='col-md-6'>
                                <div className='form-group'>
                                    <label htmlFor="ifsc">
                                        <span>IFSC </span>
                                        <input type='text' id='ifsc' className='form-control' {...register('ifsc', {})} />
                                    </label>
                                    {errors.ifsc && <span className="required">This field is required.</span>}
                                </div>
                            </div>

                            <div className='col-md-6'>
                                <div className='form-group'>
                                    <label htmlFor="bank_name">
                                        <span>Bank Name </span>
                                        <input type='text' id='bank_name' className='form-control' {...register('bank_name', {})} />
                                    </label>
                                    {errors.bank_name && <span className="required">This field is required.</span>}
                                </div>
                            </div>

                            <div className='col-md-6'>
                                <div className='form-group'>
                                    <label htmlFor="reporting-user">
                                        <span>Status <a style={{ color: "red" }}>*</a></span>
                                        <select name='status' className="form-select" {...register('status', { required: true })}>
                                            <option value="">Select Status</option>
                                            <option value="1">Active</option>
                                            <option value="2">Inactive</option>
                                        </select>
                                    </label>
                                    {errors.status && <span className="required">This field is required.</span>}
                                </div>
                            </div>
                            <div className='col-md-6'>
                                <div className='form-group'>
                                    <label>
                                        <span>Location <a style={{ color: "red" }}>*</a></span>
                                        <select
                                            className="form-select"
                                            {...register('location_id', { required: true })}
                                        >
                                            <option value="">Select Location</option>

                                            {locations.length > 0 &&
                                                locations.map((loc) => (
                                                    <option key={loc.id} value={String(loc.id)}>
                                                        {loc.name}
                                                    </option>
                                                ))
                                            }
                                        </select>
                                    </label>
                                    {errors.location_id && (
                                        <span className="required">This field is required.</span>
                                    )}
                                </div>
                            </div>
                            <div className='col-md-12'>
                                <div className='form-group mb-2'>
                                    <button className='btn btn-primary'>Submit</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>

            </Container>
        </>
    );
}

function mapStateToProps(state) {
    return {
        usersData: state.usersData,
        masterDealersData: state.masterDealersData,
        getUserModuleData: state.getUserModuleData,
    }
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators(Object.assign(userActions.default, leadActions.default, campaignsActions.default), dispatch),
    }
}
export default connect(mapStateToProps, mapDispatchToProps)(DealerAddEdit);